import { ILayer } from '../layer.type';
import { environment } from '~environments/environment';
import { getCSSColorVariableValue, _ } from '~shared/utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';

/**
 * Mikkeli
 */
export default (): TenantConfig => ({
  layerDefinitions: getLayers(),
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 61.647,
    lng: 27.241,
  },
  defaultZoom: 9.41,
  alarmFilters: [{
    id: 2,
    name: _('Category'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A',
        value: 1,
        boolean: true
      },
      {
        name: 'B',
        value: 2,
        boolean: true
      },
      {
        name: 'C',
        value: 3,
        boolean: true
      },
      {
        name: 'D',
        value: 4,
        boolean: true
      }
    ]
  }],
  vivo: {
    diaryTaskType: '5bd169ac026a0c283c0fbdcf',
  }
});

export function getLayers(): ILayer[] {
  return [
    {
      id: 'PUMP_STATION_RAINWATER',
      name: _('Rainwater pump stations'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/sadevedenpumppaamo.svg',
          fill: getCSSColorVariableValue('success-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: true,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/PUMP_STATION_RAINWATER.json',
      filter: { filters: [{ field: 'type', value: 'PUMP_STATION_RAINWATER' }] },
    },
    {
      id: 'PUMP_STATION_WASTEWATER',
      name: _('Wastewater pump stations'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/jvpumppaamo.svg',
          fill: '#C4883A',
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/PUMP_STATION_WASTEWATER.json',
      filter: {
        filters: [{ field: 'type', value: 'PUMP_STATION_WASTEWATER' }],
      },
    },
    {
      id: 'PRESSURE_STATION',
      name: _('Pressure boosting stations'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },
      useWorker: true,
      style: {
        icon: {
          url:
            environment.assetRoot + '/img/rego/pv-paineenkorotusasema.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/PRESSURE_STATION.json',
      filter: { filters: [{ field: 'type', value: 'PRESSURE_STATION' }] },
    },
    {
      id: 'WASTEWATER_PLANT',
      name: _('Wastewater plants'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/jvpuhdistamo.svg',
          fill: '#C4883A',
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/WASTEWATER_PLANT.json',
      filter: { filters: [{ field: 'type', value: 'WASTEWATER_PLANT' }] },
    },
    {
      id: 'CLEANWATER_PLANT',
      name: _('Clean water plants'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/pv-tuotantolaitos.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/CLEANWATER_PLANT.json',
      filter: { filters: [{ field: 'type', value: 'CLEANWATER_PLANT' }] },
    },
    {
      id: 'CLEANWATER_STORAGE',
      name: _('Clean water storage'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/vesitorni.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/CLEANWATER_STORAGE.json',
      filter: { filters: [{ field: 'type', value: 'CLEANWATER_STORAGE' }] },
    },
    {
      id: 'MEASURE_POINT',
      name: _('Measurement stations'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/pv-mitta-asema.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/MEASURE_POINT.json',
      filter: { filters: [{ field: 'type', value: 'MEASURE_POINT' }] },
    },
    {
      id: 'FLOW_SUSPECT',
      name: _('Leak detection'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/vuotoepaily.svg',
          fill: getCSSColorVariableValue('danger-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/FLOW_SUSPECT.json',
      filter: { filters: [{ field: 'type', value: 'FLOW_SUSPECT' }] },
    },
    {
      id: 'ANALYZE_POINT_WATER_CONSUMPTION',
      name: _('Residential water analysis points'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/analyysipiste.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/ANALYZE_POINT_WATER_CONSUMPTION.json',
      filter: {
        filters: [{ field: 'type', value: 'ANALYZE_POINT_WATER_CONSUMPTION' }],
      },
    },
    {
      id: 'ANALYZE_POINT_WATER_HYDROLOGICAL',
      name: _('Natural water analysis points'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/analyysipiste.svg',
          fill: getCSSColorVariableValue('teal'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/ANALYZE_POINT_WATER_HYDROLOGICAL.json',
      filter: {
        filters: [{ field: 'type', value: 'ANALYZE_POINT_WATER_HYDROLOGICAL' }],
      },
    },
    {
      id: 'ANALYZE_POINT_WATER_OBSERVATION_TUBE',
      name: _('Observation tubes'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/analyysipiste.svg',
          fill: getCSSColorVariableValue('teal'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/ANALYZE_POINT_WATER_OBSERVATION_TUBE.json',
      filter: {
        filters: [
          { field: 'type', value: 'ANALYZE_POINT_WATER_OBSERVATION_TUBE' },
        ],
      },
    },
    {
      id: 'OXYGENERATOR',
      name: _('Oxidizer'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/hapetin.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T8/points/OXYGENERATOR.json',
      filter: { filters: [{ field: 'type', value: 'OXYGENERATOR' }] },
    },
    {
      id: 'OTHER',
      name: _('Other'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        minimumSearchTermLength: 3,
      },

      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/hapetin.svg',
          fill: getCSSColorVariableValue('info-base'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL: environment.assetRoot + '/data/T8/points/OTHER.json',
      filter: { filters: [{ field: 'type', value: 'OTHER' }] },
    },
    {
      id: 'Hulevesiverkosto',
      name: _('Urban runoff network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector'
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['build_year'] },
        uniqueBy: 'id',
        labelBy: 'build_year',
        visibleByZoom: 10,
      },
      searchOptions: {
        searchable: false,
        interestingProperties: [{ key: 'build_year', type: 'contains' }]
      },
      useWorker: true,
    },
    {
      id: 'Salaojaverkosto',
      name: _('Subsurface drain network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector'
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['build_year', 'material'],
        },
        uniqueBy: 'id',
        labelBy: 'build_year',
      },
      searchOptions: {
        searchable: false,
        interestingProperties: [
          { key: 'build_year', type: 'contains' },
          { key: 'material', type: 'contains' },
        ]
      },
      useWorker: true,
    },
    {
      id: 'Puhdasvesiverkosto',
      name: _('Clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('info-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector'
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['build_year', 'length'],
        },
        uniqueBy: 'id',
        labelBy: 'build_year',
      },
      searchOptions: {
        searchable: false,
        interestingProperties: [
          { key: 'build_year', type: 'contains' },
          { key: 'length', type: 'contains' },
        ]
      },
      useWorker: true,
    },
    {
      id: 'Viemäriverkosto',
      name: _('Sewer network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#cf923b',
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector'
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['build_year'] },
        uniqueBy: 'id',
        labelBy: 'build_year',
        visibleByZoom: 10
      },
      searchOptions: {
        searchable: false,
        interestingProperties: [{ key: 'build_year', type: 'contains' }],
      },
      useWorker: true,
    },
    {
      id: 'Paineviemäriverkosto',
      name: _('Pressure sewer network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#895e2a',
        strokeWeight: 4.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector'
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['build_year', 'material'],
        },
        uniqueBy: 'id',
        labelBy: 'build_year',
      },
      searchOptions: {
        searchable: false,
        interestingProperties: [
          { key: 'build_year', type: 'contains' },
          { key: 'material', type: 'contains' },
        ],
      },
      useWorker: true,
    },
    {
      id: 'Toiminta-alueet',
      name: _('Areas of operation'),
      orderingWeight: 0,
      belowRoadLayers: true,
      selectable: true,
      style: {
        sourceType: 'vector',
        layerType: 'line',
        clickable: true,
        strokeColor: 'rgb(74, 74, 74)',
        visible: false,
        strokeWeight: 4,
      },
      metadata: {
        uniqueBy: 'EntityHand',
        labelBy: 'Layer',

        visibleByZoom: 8,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: [
            'EntityHand',
            'ExtendedEn',
            'Layer',
            'Linetype'
          ]
        },
      },
      searchOptions: {
        searchable: false
      },
      useWorker: true,
    },
    {
      id: 'Vesiosuuskunnat',
      name: _('Water cooperatives'),
      orderingWeight: 0,
      belowRoadLayers: true,
      selectable: true,
      style: {
        sourceType: 'vector',
        layerType: 'line',
        clickable: true,
        strokeColor: 'rgb(0, 14, 122)',
        strokeWeight: 4,
        visible: false,
      },
      metadata: {
        uniqueBy: 'Id',
        labelBy: 'Nimi',
        visibleByZoom: 8,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: [
            'Id',
            'Nro',
            'Nimi',
            'Laji',
            'Linetype',
            'Layer',
            'Docpath',
          ]
        },
      },
      searchOptions: {
        searchable: false
      },
      useWorker: true,
    },
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    getSharedLayer('groundWaterAreas'),
    getSharedLayer('drainageAreas'),
    getSharedLayer('basinOutlets'),
    getSharedLayer('houseNumbers'),
  ];
}
